import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Calendar, Clock, FileCheck } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const BlogLadyBirdRequirements = () => {
  return (
    <>
      <Helmet>
        <title>Lady Bird Deed Requirements Florida — What You Need to Know</title>
        <meta name="description" content="Lady Bird Deed requirements in Florida: who qualifies, documents needed, process and timeline. Get your deed done right. Call 941-206-2223 for free consultation." />
        <link rel="canonical" href="https://thelawyerlady.com/blog/lady-bird-deed-requirements-florida" />
        <meta property="og:title" content="Lady Bird Deed Requirements Florida — What You Need to Know" />
        <meta property="og:description" content="Lady Bird Deed requirements in Florida: who qualifies, documents needed, process and timeline. Get your deed done right." />
        <meta property="og:url" content="https://thelawyerlady.com/blog/lady-bird-deed-requirements-florida" />
        <meta property="og:type" content="article" />
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thelawyerlady.com" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://thelawyerlady.com/blog" },
            { "@type": "ListItem", "position": 3, "name": "Requirements", "item": "https://thelawyerlady.com/blog/lady-bird-deed-requirements-florida" }
          ]
        })}
        </script>
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Lady Bird Deed Requirements in Florida — What You Need to Know",
          "author": { "@type": "Person", "name": "Season A. Spanski" },
          "publisher": { "@type": "Organization", "name": "Spanski Law, PLLC" },
          "datePublished": "2025-01-20",
          "dateModified": "2025-01-20"
        })}
        </script>
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Who can create a Lady Bird Deed in Florida?",
              "acceptedAnswer": { "@type": "Answer", "text": "Any Florida property owner who is of sound mind and at least 18 years old can create a Lady Bird Deed. If the property is owned jointly, all owners must sign." }
            },
            {
              "@type": "Question",
              "name": "Do I need my spouse's signature for a Lady Bird Deed?",
              "acceptedAnswer": { "@type": "Answer", "text": "If your spouse's name is on the deed, they must sign. If the property is your homestead in Florida, your spouse typically must sign even if they're not on the deed." }
            }
          ]
        })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Breadcrumb */}
        <div className="max-w-3xl mx-auto px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-sm text-[#faf9f7]/60">
            <Link to="/" className="hover:text-[#c9a87c] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-[#c9a87c] transition-colors">Blog</Link>
            <span>/</span>
            <span className="text-[#c9a87c]">Requirements</span>
          </nav>
        </div>

        {/* Article Header */}
        <header className="max-w-3xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4 text-sm text-[#faf9f7]/60 mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>January 20, 2025</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>8 min read</span>
            </div>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#faf9f7] mb-6">
            Lady Bird Deed Requirements in Florida — What You Need to Know
          </h1>
          <p className="font-sans text-xl text-[#faf9f7]/70 leading-relaxed">
            If you're considering a Lady Bird Deed for your Florida property, here's what you need: who qualifies, what documents are required, and how the process works from start to finish.
          </p>
        </header>

        {/* Article Content */}
        <article className="max-w-3xl mx-auto px-6 py-8">
          <div className="prose prose-lg prose-invert max-w-none">
            
            <h2 className="font-serif text-2xl text-[#faf9f7] mb-4 mt-8">Who Can Create a Lady Bird Deed</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              To create a Lady Bird Deed in Florida, you must:
            </p>
            <ul className="list-disc pl-6 text-[#faf9f7]/80 space-y-2 mb-6">
              <li><strong>Be of legal age:</strong> You must be at least 18 years old.</li>
              <li><strong>Be of sound mind:</strong> You must have the mental capacity to understand what you're doing — who your beneficiaries are, what property you're transferring, and what the deed means.</li>
              <li><strong>Own Florida real estate:</strong> Lady Bird Deeds only work for property located in Florida.</li>
              <li><strong>Have clear title:</strong> You need to actually own the property you're transferring. If there are title issues, those need to be resolved first.</li>
            </ul>

            <h2 className="font-serif text-2xl text-[#faf9f7] mb-4 mt-8">What About Married Couples?</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              If you're married and the property is your Florida homestead (where you live), your spouse typically needs to sign the Lady Bird Deed — even if they're not on the current deed. This is because of Florida's strong homestead protections.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              If you own the property jointly with your spouse (or anyone else), all owners must sign the Lady Bird Deed.
            </p>

            <h2 className="font-serif text-2xl text-[#faf9f7] mb-4 mt-8">What Properties Qualify</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Lady Bird Deeds work for most types of Florida real estate:
            </p>
            <ul className="list-disc pl-6 text-[#faf9f7]/80 space-y-2 mb-6">
              <li>Single-family homes</li>
              <li>Condominiums</li>
              <li>Townhouses</li>
              <li>Vacant land</li>
              <li>Investment properties</li>
              <li>Commercial property</li>
            </ul>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              The property must be located in Florida. If you own property in another state, that state's laws apply, and most states don't recognize Lady Bird Deeds.
            </p>

            <h2 className="font-serif text-2xl text-[#faf9f7] mb-4 mt-8">Documents and Information Needed</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              When you work with me, I'll need:
            </p>
            
            <div className="bg-[#141414] border border-[#c9a87c]/20 rounded-lg p-6 my-6">
              <div className="flex items-center gap-3 mb-4">
                <FileCheck className="w-6 h-6 text-[#c9a87c]" />
                <h3 className="font-serif text-xl text-[#faf9f7] m-0">Required Information</h3>
              </div>
              <ul className="list-disc pl-6 text-[#faf9f7]/80 space-y-2">
                <li><strong>Current deed:</strong> A copy of your existing deed showing how you currently own the property.</li>
                <li><strong>Property address:</strong> The full address and legal description of the property.</li>
                <li><strong>Owner information:</strong> Full legal names of all current owners, plus marital status.</li>
                <li><strong>Beneficiary information:</strong> Full legal names and addresses of everyone who will inherit the property.</li>
                <li><strong>Mortgage information:</strong> Whether there's a mortgage on the property.</li>
                <li><strong>Homestead status:</strong> Whether this is your primary residence.</li>
              </ul>
            </div>

            <h2 className="font-serif text-2xl text-[#faf9f7] mb-4 mt-8">The Process Step by Step</h2>
            
            <h3 className="font-serif text-xl text-[#c9a87c] mb-3 mt-6">Step 1: Initial Consultation</h3>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              We start with a phone or video call where I learn about your property, your goals, and who you want to inherit. I'll confirm that a Lady Bird Deed is the right solution and explain exactly how it works.
            </p>

            <h3 className="font-serif text-xl text-[#c9a87c] mb-3 mt-6">Step 2: Deed Preparation</h3>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              I draft your Lady Bird Deed using Florida-specific language that preserves your rights. This includes the "enhanced" provisions that let you sell, mortgage, or revoke without your beneficiaries' consent.
            </p>

            <h3 className="font-serif text-xl text-[#c9a87c] mb-3 mt-6">Step 3: Review</h3>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              I send you the draft to review. You'll check that the names, property description, and beneficiaries are all correct. If anything needs to change, I'll revise it.
            </p>

            <h3 className="font-serif text-xl text-[#c9a87c] mb-3 mt-6">Step 4: Signing and Notarization</h3>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              The deed must be signed in front of a notary public and two witnesses. You can do this:
            </p>
            <ul className="list-disc pl-6 text-[#faf9f7]/80 space-y-2 mb-6">
              <li>In person at my Venice office</li>
              <li>At a local notary or bank</li>
              <li>Through remote online notarization (from your computer)</li>
            </ul>

            <h3 className="font-serif text-xl text-[#c9a87c] mb-3 mt-6">Step 5: Recording</h3>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Once signed, I submit the deed to your county's Clerk of Court for recording. This makes it part of the official public record. I'll send you a recorded copy for your files.
            </p>

            <h2 className="font-serif text-2xl text-[#faf9f7] mb-4 mt-8">Timeline</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              The typical timeline from first call to recorded deed is 1-2 weeks. If you need it faster, let me know and I'll do what I can to expedite.
            </p>

            <h2 className="font-serif text-2xl text-[#faf9f7] mb-4 mt-8">What Happens at Death</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              When you pass away, your beneficiaries don't have to do much:
            </p>
            <ol className="list-decimal pl-6 text-[#faf9f7]/80 space-y-2 mb-6">
              <li>Obtain a certified copy of your death certificate</li>
              <li>Record the death certificate with the county clerk where the property is located</li>
              <li>The property is now theirs — no probate needed</li>
            </ol>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Learn more about <Link to="/blog/does-lady-bird-deed-avoid-probate-florida" className="text-[#c9a87c] hover:underline">how Lady Bird Deeds avoid probate</Link>.
            </p>

            <h2 className="font-serif text-2xl text-[#faf9f7] mb-4 mt-8">Ready to Get Started?</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              I'm Season Spanski, and I prepare Lady Bird Deeds for homeowners throughout Florida. Learn more about my <Link to="/lady-bird-deed-florida" className="text-[#c9a87c] hover:underline">Lady Bird Deed services</Link> or call 941-206-2223 to schedule a free consultation.
            </p>

          </div>
        </article>

        {/* CTA Section */}
        <section className="max-w-3xl mx-auto px-6 py-16">
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#c9a87c]/20 rounded-lg p-8 md:p-12 text-center">
            <h2 className="font-serif text-3xl text-[#faf9f7] mb-4">Ready to Protect Your Florida Home?</h2>
            <p className="text-[#faf9f7]/70 mb-8">
              Call 941-206-2223 or schedule a free consultation online. I'll answer your questions and explain exactly what you need to get started.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:941-206-2223" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold rounded-sm hover:bg-[#d4b896]">
                <Phone className="w-5 h-5" />
                Call 941-206-2223
              </a>
              <Link to="/#contact" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#c9a87c] text-[#c9a87c] font-sans font-medium rounded-sm hover:bg-[#c9a87c]/10">
                Schedule Online
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogLadyBirdRequirements;
